import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  EmployerCostSettingFlagForm,
  EmployerCostSettingsFormValues,
  EmployerCostSettingsRequest
} from '../types';

import { schema } from '../utils/schema';
import { convertFlagsObjectToArray } from 'utils/misc';

import { LoadingButton } from '@mui/lab';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { useSave } from '../hooks/useSave';
import GeneralSection from './Sections/General/GeneralSection';
import HoursBalanceSection from './Sections/HoursBalance/HoursBalanceSection';
import SurchargesSection from './Sections/Surcharges/SurchargesSection';
import { percentageToDecimalNumber } from '../utils/percentageToDecimal';

/**
 * @description Decimal fields that were converted to percentage fields for simplified input
 */
export const percentageFields = ['finEAgehoursPrc', 'finEAgehoursBuhPrc'];

interface Props {
  data: EmployerCostSettingsFormValues | undefined;
  handleClose: () => void;
  isCopy: boolean;
}

const EmployerCostSettingsForm = (props: Props) => {
  const { data: employerCostSettings, handleClose, isCopy } = props;
  const { t } = useTranslation('common');
  const { mutate: updateEmployerCostSettings, isLoading } =
    useSave(handleClose);

  const methods = useForm({
    defaultValues: schema.cast(employerCostSettings),
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  const convertFormDataToRequest = (
    data: EmployerCostSettingsFormValues
  ): EmployerCostSettingsRequest => {
    const formData = { ...data };
    // Percentage fields are converted back to decimal fields
    percentageFields.forEach(
      (field: string) =>
        (formData[field] = percentageToDecimalNumber(
          methods.watch(field as any)
        ))
    );

    return {
      ...formData,
      flags: convertFlagsObjectToArray<EmployerCostSettingFlagForm>(data.flags)
    };
  };

  const onSubmit = (data) => {
    updateEmployerCostSettings({
      data: convertFormDataToRequest(data),
      isCopy: isCopy
    });
  };

  return (
    <MultiStepForm methods={methods} onSubmit={onSubmit}>
      <GeneralSection />
      <SurchargesSection />
      <HoursBalanceSection />
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="dark"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default EmployerCostSettingsForm;
